import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowsHorizontal } from "@phosphor-icons/react";

/**
 * Drag-to-reveal before/after slider.
 * - Left ("before") and right ("after") children are stacked and revealed via a clip-path divider.
 * - Divider is draggable by mouse, touch, or keyboard (left/right arrows).
 */
export function BeforeAfterSlider({ before, after, initial = 50, className = "" }) {
  const [pct, setPct] = useState(Math.max(0, Math.min(100, initial)));
  const [dragging, setDragging] = useState(false);
  const wrapRef = useRef(null);

  const setFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(0, Math.min(100, next)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, setFromClientX]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); setPct(p => Math.max(0, p - 4)); }
    if (e.key === "ArrowRight") { e.preventDefault(); setPct(p => Math.min(100, p + 4)); }
  };

  return (
    <div
      ref={wrapRef}
      onMouseDown={(e) => { setDragging(true); setFromClientX(e.clientX); }}
      onTouchStart={(e) => { setDragging(true); setFromClientX(e.touches[0].clientX); }}
      className={`relative select-none overflow-hidden border-4 border-[#0f172a] shadow-[10px_10px_0_#0f172a] bg-[#0f172a] ${dragging ? "cursor-grabbing" : "cursor-grab"} ${className}`}
      data-testid="before-after-slider"
    >
      {/* BEFORE — base layer, always fully rendered so its visible portion is at left of the handle */}
      <div className="absolute inset-0" data-testid="before-layer">
        {before}
        <div className="absolute top-3 left-3 h-7 px-2.5 bg-red-500 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
          BEFORE
        </div>
      </div>

      {/* AFTER — clipped from the right of the handle */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 0 0 ${pct}%)` }}
        data-testid="after-layer"
      >
        {after}
        <div className="absolute top-3 right-3 h-7 px-2.5 bg-emerald-500 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
          AFTER
        </div>
      </div>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-1 h-full bg-white shadow-[0_0_0_2px_#0f172a]"/>
      </div>
      <button
        type="button"
        onKeyDown={onKey}
        aria-label="Drag to compare before and after"
        data-testid="before-after-handle"
        style={{ left: `${pct}%`, transform: "translate(-50%, -50%)" }}
        className={`absolute top-1/2 h-14 w-14 rounded-full bg-white border-4 border-[#0f172a] shadow-[4px_4px_0_#0f172a] flex items-center justify-center text-[#0f172a] focus:outline-none focus:ring-4 focus:ring-[#00C4CC] ${dragging ? "scale-105" : ""} transition-transform`}
      >
        <ArrowsHorizontal weight="bold" size={22}/>
      </button>

      {/* Hint */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 h-7 px-3 bg-[#0f172a]/85 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5 pointer-events-none">
        Drag to compare
      </div>
    </div>
  );
}

export default BeforeAfterSlider;
